import { driver } from 'driver.js';
import { useEffect, useRef } from 'react';
import { createGlobalStyle } from 'styled-components';

import 'driver.js/dist/driver.css';
import elevateLogo from '@/assets/logo.svg';
import { isLocalStorageAvailable } from '@/utils';

const GlobalStepsStyle = createGlobalStyle`
	.driver-popover-close-btn {
		border-radius: 50%;
		color: var(--elevate-primary);
		flex-shrink: 0;
		font-weight: 500;
		height: 25px;
		width: 25px;
		margin: 4px;

		&:focus,
		&:hover {
			background-color: var(--input-border);
		}
	}

	.driver-popover-title {
		color: var(--elevate-primary);
		font-family: var(--font-semibold);
	}

	.driver-popover {
		padding: 24px 16px;
	}

	.driver-popover-description {
		color: var(--elevate-primary);
		font-family: var(--font-regular);
		margin-bottom: 16px;

		img {
			max-width: 200px;
			margin: auto;
		}
	}

	.driver-popover-navigation-btns button+button {
		margin-left: var(--spacing-4);
	}

	.driver-popover-footer button {
		border-radius: 6px;
		border: 2px solid;
		cursor: pointer;
		font-family: var(--font-semibold);
		font-size: 15px;
		line-height: 1;
		padding: var(--spacing-2) var(--spacing-4);
		text-shadow: none;
		transition: background-color 0.3s, box-shadow 0.3s, color 0.3s, border-color 0.3s;

		&:hover,
		&:focus {
			background-color: var(--wine-btn-active);
			border-color: var(--wine-btn-active);
			box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14),
				0 1px 5px 0 rgba(0, 0, 0, 0.12);
				color: var(--c-white);
			outline: none;
		}
	}

	button.driver-popover-next-btn {
		background-color: var(--wine-btn);
		border-color: var(--wine-btn);
		color: var(--neutral-50);
		cursor: pointer;
	}

	button.driver-popover-prev-btn {
		background-color: transparent;
		border-color: var(--wine-btn);
		color: var(--wine-btn);
	}

	.has-default-height {
		display: flex !important;
		flex-direction: column;
		min-height: 275px;

		.driver-popover-footer {
			margin-top: auto;
		}
	}
`;

interface AdminCourseMapIntroProps {
	isEnabled: boolean;
	setIsEnabled: (value: boolean) => void;
}

const AdminCourseMapIntro = ({ isEnabled, setIsEnabled }: AdminCourseMapIntroProps) => {
	// Guard so drive() only fires once per isEnabled=true activation.
	// Without this, any React re-render while isEnabled is true (e.g. caused by
	// a parent state change triggered from inside an onNextClick handler) would
	// create a fresh driverObj and call drive() again, restarting the tour from
	// step 0 and causing elements to not be found mid-tour.
	const didStartRef = useRef(false);

	useEffect(() => {
		if (!isEnabled) {
			didStartRef.current = false;
			return;
		}

		if (didStartRef.current) return;
		didStartRef.current = true;

		const handleExit = () => {
			if (isLocalStorageAvailable()) {
				localStorage.setItem('elevateAdminCourseMapTutorial', 'false');
			}
			setIsEnabled(false);
		};

		const driverObj = driver({
			showProgress: true,
			nextBtnText: 'Next',
			prevBtnText: 'Previous',
			onDestroyed: handleExit,
			steps: [
				{
					popover: {
						popoverClass: 'has-default-height',
						description: `<img src=${elevateLogo}><br />Welcome to the <strong>Admin Course Map</strong>. This is your hub for building and managing your curriculum. Press Next to learn what's new.`,
						align: 'center',
					},
				},
				{
					element: '#cm-toolbar',
					popover: {
						title: 'Map Toolbar',
						description:
							'The toolbar sits at the top of the map and gives you quick access to every control — from opening the course panel to saving your changes.',
						align: 'center',
					},
				},
				{
					element: '#cm-mode-segmented',
					popover: {
						title: 'Map Modes',
						description:
							'<strong>View</strong> — freely pan and zoom without accidentally editing anything.<br /><br /><strong>Edit</strong> — drag courses to rearrange them and click <strong>+</strong> to add a course below any node.<br /><br /><strong>Remove</strong> — click <strong>−</strong> on a card to remove that course from the map.',
						align: 'center',
					},
				},
				{
					element: '#cm-course-panel-btn',
					popover: {
						title: 'Course Panel',
						description:
							'Open the Course Panel to browse all available courses and disciplines. In <strong>Edit</strong> mode you can also click the <strong>+</strong> icon directly on any card for a quick search popover.',
						align: 'start',
					},
				},
				{
					popover: {
						title: 'The Map Canvas',
						description:
							'Pan by clicking and dragging. Zoom with the scroll wheel or pinch gesture. In <strong>Edit</strong> mode you can also grab any course card and drag it to a new branch. All descendants will move with it.',
						align: 'center',
					},
				},
				{
					popover: {
						title: 'Discipline & Course Cards',
						description:
							'Each card represents a discipline (top level) or an individual course. Switch to <strong>Edit</strong> mode and hover a card to see the <strong>+</strong> (add) and <strong>M</strong> (merge) action buttons appear on its edges.',
						align: 'end',
					},
				},
				{
					element: '#cm-undo-redo',
					popover: {
						title: 'Undo & Redo',
						description:
							'Every edit is tracked. Click <strong>Undo</strong> or press <strong>⌘Z</strong> to step back. <strong>Redo</strong> (<strong>⌘⇧Z</strong>) reapplies a change. You can step through your full edit history before committing anything.',
						align: 'start',
					},
				},
				{
					element: '#cm-help-btn',
					popover: {
						title: 'Replay This Tour',
						description:
							'You can re-open this tutorial at any time by clicking the <strong>?</strong> button in the toolbar.',
						align: 'start',
					},
				},
			],
		});

		// Reset the map view so targeted elements are in the viewport before the tour starts
		(document.querySelector('#pan-reset') as HTMLButtonElement)?.click();
		driverObj.drive();
	}, [isEnabled, setIsEnabled]);

	return <GlobalStepsStyle />;
};

export default AdminCourseMapIntro;
